import { categoryDescendants, type Account, type Category, type Payee, type Tag } from '@keel/core';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { errorMessage, useTRPC, useTRPCClient } from '@/api/query';
import { toast, type ComboboxOption } from '@/components/ui';
import { useSession } from '@/lib/session';

const byId = <T extends { id: string }>(rows: readonly T[] | undefined): ReadonlyMap<string, T> => new Map((rows ?? []).map((r) => [r.id, r]));

/**
 * The ledger reference data most money screens need (accounts incl. closed, categories, payees, tags) for the
 * current entity, plus id → row maps. Queries are shared through the TanStack cache, so calling this from
 * several components costs one request each.
 */
export const useLedger = () => {
  const trpc = useTRPC();
  const { entityId, entity, canEdit } = useSession();
  const input = entityId ? { entityId } : skipToken;
  const accounts = useQuery(trpc.accounts.list.queryOptions(entityId ? { entityId, includeClosed: true } : skipToken));
  const categories = useQuery(trpc.categories.list.queryOptions(input));
  const payees = useQuery(trpc.payees.list.queryOptions(input));
  const tags = useQuery(trpc.tags.list.queryOptions(input));
  const a: readonly Account[] = accounts.data ?? [];
  const c: readonly Category[] = categories.data ?? [];
  const p: readonly Payee[] = payees.data ?? [];
  const t: readonly Tag[] = tags.data ?? [];
  const maps = useMemo(
    () => ({ accountById: byId(a), categoryById: byId(c), payeeById: byId(p), tagById: byId(t) }),
    [a, c, p, t],
  );
  return {
    entityId, entity, canEdit,
    accounts: a, openAccounts: a.filter((x) => !x.isClosed), categories: c, payees: p, tags: t, ...maps,
    loading: accounts.isPending || categories.isPending,
    baseCurrency: entity?.baseCurrency ?? 'AUD',
  };
};

/**
 * Invalidate every cached query after a ledger mutation. ponytail: blunt but always correct (balances, budgets,
 * goals, home and reports all derive from transactions); only active queries refetch. Narrow it if it shows up in profiles.
 */
export const useRefresh = () => {
  const qc = useQueryClient();
  return useCallback(() => qc.invalidateQueries(), [qc]);
};

/** For a category picker's `onCreate`: adds a top-level category by name (income for money in, else expense). */
export const useCreateCategory = (kind: Category['kind'] = 'expense') => {
  const client = useTRPCClient();
  const { entityId, canEdit } = useSession();
  const refresh = useRefresh();
  const create = useCallback(async (name: string): Promise<ComboboxOption | null> => {
    if (!entityId) return null;
    try {
      const c = await client.categories.create.mutate({ entityId, name: name.slice(0, 80), kind });
      void refresh();
      toast.success(`Added category ${c.name}`);
      return { value: c.id, label: c.name };
    } catch (e) {
      toast.error(errorMessage(e));
      return null;
    }
  }, [client, entityId, kind, refresh]);
  return canEdit ? create : undefined;
};

// ───────────────────────── category tree ─────────────────────────
export interface CategoryNode { readonly category: Category; readonly depth: number; readonly path: string }

/** Depth-first flattened tree (parents before children, siblings by sortOrder then name). */
export const categoryTree = (categories: readonly Category[]): CategoryNode[] => {
  const kids = new Map<string | null, Category[]>();
  const ids = new Set(categories.map((c) => c.id));
  for (const c of categories) {
    const parent = c.parentId && ids.has(c.parentId) ? c.parentId : null;
    kids.set(parent, [...(kids.get(parent) ?? []), c]);
  }
  for (const list of kids.values()) list.sort((x, y) => x.sortOrder - y.sortOrder || x.name.localeCompare(y.name));
  const out: CategoryNode[] = [];
  const walk = (parent: string | null, depth: number, path: string, seen: Set<string>) => {
    for (const c of kids.get(parent) ?? []) {
      if (seen.has(c.id)) continue;
      const p = path ? `${path} › ${c.name}` : c.name;
      out.push({ category: c, depth, path: p });
      walk(c.id, depth + 1, p, new Set([...seen, c.id]));
    }
  };
  walk(null, 0, '', new Set());
  return out;
};

/** Combobox options for categories: name as label, parent path as hint; hidden ones excluded unless asked. */
export const categoryOptions = (
  categories: readonly Category[],
  opts: { readonly kinds?: readonly Category['kind'][]; readonly includeHidden?: boolean } = {},
): ComboboxOption[] =>
  categoryTree(categories)
    .filter((n) => (opts.includeHidden || !n.category.isHidden) && (!opts.kinds || opts.kinds.includes(n.category.kind)))
    .map((n) => ({
      value: n.category.id,
      label: n.category.name,
      hint: n.depth > 0 ? n.path.split(' › ').slice(0, -1).join(' › ') : n.category.kind === 'income' ? 'Income' : undefined,
      keywords: [n.path, n.category.kind],
    }));

/** The category plus all its descendants (filtering by a parent includes its children). */
export const withDescendants = (categories: readonly Category[], ids: readonly string[]): string[] =>
  [...new Set(ids.flatMap((id) => [...categoryDescendants(categories, id)]))];

export const accountOptions = (accounts: readonly Account[], opts: { readonly includeClosed?: boolean } = {}): ComboboxOption[] =>
  accounts
    .filter((a) => opts.includeClosed || !a.isClosed)
    .map((a) => ({ value: a.id, label: a.name, hint: a.institution ?? undefined, keywords: [a.type, a.currency] }));

/** Payees made from business contacts (API `contactKind`) say so: "Customer" / "Supplier". */
export const contactHint = (p: Payee & { readonly contactKind?: string | null }): string | undefined =>
  p.contactKind === 'customer' ? 'Customer' : p.contactKind === 'supplier' ? 'Supplier' : p.contactKind === 'both' ? 'Customer & supplier' : undefined;

export const payeeOptions = (payees: readonly Payee[]): ComboboxOption[] =>
  payees.map((p) => ({ value: p.id, label: p.name, hint: contactHint(p), keywords: p.aliases }));

/**
 * Transfer destinations in other entities the user can edit (API `accounts.transferTargets`), as combobox options
 * hinted with the entity name — business ↔ personal transfers become drawings / owner contributions on the server.
 */
export const useOtherEntityTargets = (entityId: string | null | undefined, enabled = true) => {
  const trpc = useTRPC();
  const q = useQuery(trpc.accounts.transferTargets.queryOptions(entityId && enabled ? { entityId } : skipToken, { meta: { silent: true } }));
  return useMemo(() => {
    const others = (q.data ?? []).filter((a) => !a.sameEntity);
    return {
      options: others.map((a): ComboboxOption => ({ value: a.id, label: a.name, hint: a.entityName, keywords: [a.entityName, a.type, a.currency] })),
      byId: new Map(others.map((a) => [a.id, a])),
    };
  }, [q.data]);
};
